/**
 * Role-aware layout. Renders the navigation sections the current user is
 * entitled to:
 *
 *   - sales/lead role → 工作台 (sales pages)
 *   - admin role     → 工作台 + 管理 (full admin pages)
 *
 * Replaces the previous AdminLayout. Routes are still defined centrally in
 * main.tsx; this layout just controls visibility.
 */

import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';

import { setToken } from '../lib/api';
import { roleSatisfies, useCurrentUser } from '../lib/useCurrentUser';
import { NotificationBell } from './NotificationBell';

interface NavItem {
  to: string;
  label: string;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const SALES_GROUPS: NavGroup[] = [
  {
    title: '首页',
    items: [{ to: '/sales/leads', label: '我的线索' }],
  },
];

const ADMIN_GROUPS: NavGroup[] = [
  {
    title: '首页',
    items: [{ to: '/admin/dashboard', label: '运营总览' }],
  },
  {
    title: '数据源（TG）',
    items: [
      { to: '/admin/proxies', label: '代理池' },
      { to: '/admin/accounts', label: 'TG 账号' },
      { to: '/admin/groups', label: '已加入的群' },
      { to: '/admin/discovery', label: '资源发现' },
      { to: '/admin/expansion', label: '扩散计划' },
    ],
  },
  {
    title: '判断规则',
    items: [
      { to: '/admin/filters', label: '关键词过滤' },
      { to: '/admin/tenant-config', label: '业务描述与阈值' },
      { to: '/admin/noise-anchors', label: '噪声样本库' },
    ],
  },
  {
    title: '线索与销售',
    items: [
      { to: '/admin/leads', label: '全部线索（只读）' },
      { to: '/admin/sales-users', label: '销售员账号' },
    ],
  },
  {
    title: '用量与日志',
    items: [
      { to: '/admin/llm-usage', label: '模型调用用量' },
      { to: '/admin/audit', label: '操作审计' },
    ],
  },
  {
    title: '系统与调试',
    items: [
      { to: '/admin/system-config', label: '系统配置' },
      { to: '/admin/raw-messages', label: '原始消息浏览' },
      { to: '/admin/cascade/l1-passed', label: '通过 L1 的消息' },
      { to: '/admin/cascade/l2-passed', label: '通过 L2 的消息' },
      { to: '/admin/cascade/l3-dropped', label: 'L3 否决（漏判分析）' },
      { to: '/admin/dead-letters', label: '死信队列' },
    ],
  },
];

export function AppLayout() {
  const navigate = useNavigate();
  const me = useCurrentUser();
  const role = me.data?.role ?? 'sales';
  const onLogout = () => {
    setToken(null);
    navigate('/login', { replace: true });
  };

  const showSales = roleSatisfies(role, 'sales');
  const showAdmin = roleSatisfies(role, 'admin');

  return (
    <div className="min-h-screen flex">
      <aside className="w-56 bg-slate-900 text-slate-100 flex flex-col">
        <div className="px-6 py-5 border-b border-slate-800">
          <Link to="/" className="text-lg font-semibold">
            TG Leads
          </Link>
          <div className="text-xs text-slate-400 mt-1">
            {me.data ? `${me.data.name} · ${role}` : '加载中…'}
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto">
          {showSales &&
            SALES_GROUPS.map((g) => (
              <NavSection key={g.title} title={g.title} items={g.items} />
            ))}
          {showAdmin &&
            ADMIN_GROUPS.map((g) => (
              <NavSection key={g.title} title={g.title} items={g.items} />
            ))}
        </nav>
        <button onClick={onLogout} className="m-3 btn-secondary !text-slate-700">
          退出登录
        </button>
      </aside>
      <main className="flex-1 overflow-auto">
        <div className="flex justify-end px-8 pt-4">
          <NotificationBell />
        </div>
        <div className="px-8 pb-8 pt-2">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function NavSection({ title, items }: { title: string; items: NavItem[] }) {
  return (
    <div className="space-y-1">
      <div className="px-3 text-[10px] uppercase tracking-wider text-slate-500 font-medium">
        {title}
      </div>
      {items.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          className={({ isActive }) =>
            `block px-3 py-2 rounded text-sm transition ${
              isActive
                ? 'bg-indigo-600 text-white'
                : 'text-slate-300 hover:bg-slate-800'
            }`
          }
        >
          {n.label}
        </NavLink>
      ))}
    </div>
  );
}
